import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import NotFound from "components/not-found";
import { getConfigVars } from "reducer";
import { createApiClient } from "utils";
import { fetchCommunityRequests } from "utils/fetch-community-requests";
import { fetchCommunityQuarterlySummary } from "utils/fetch-community-quarterly-summary";
import { mockCommunityRequests } from "mocks/community-requests";
import { fetchExportUrl } from "utils/fetch-export-url";
import { mockExportUrlResponse } from "mocks/export-url";

import {
  CommunityRequestsTable,
  Hero,
  Carousel,
  TextDetail,
  FaqAccordion,
  SummaryMetrics
} from "components/impact-report";
import { Container, ContentWrapper } from "./index.styled";
import mobileHeroImage from "../../assets/images/impact-report_hero_mobile.png";
import desktopHeroImage from "../../assets/images/impact-report_hero_desktop.png";

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

const fetchWithRetry = async (fetchFn, maxRetries = MAX_RETRIES) => {
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;
      console.error(
        `Fetch attempt ${attempt + 1} of ${maxRetries + 1} failed:`,
        error
      );

      if (attempt < maxRetries) {
        await new Promise(resolve =>
          setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1))
        );
      }
    }
  }

  throw lastError;
};

const calculateRefreshDelay = expiresAt => {
  const expirationTime = new Date(expiresAt).getTime();
  const now = Date.now();
  const refreshTime = expirationTime - REFRESH_BUFFER_MS;
  const delay = refreshTime - now;
  return Math.max(0, delay);
};

const ImpactReportContainer = () => {
  const [communityRequests, setCommunityRequests] = useState([]);
  const [exportUrl, setExportUrl] = useState(null);
  const [quarterlySummary, setQuarterlySummary] = useState({});
  const [isLoadingExport, setIsLoadingExport] = useState(true);
  const [hasExportError, setHasExportError] = useState(false);

  const config = useSelector(getConfigVars);
  const api = useMemo(() => createApiClient(), []);

  useEffect(() => {
    document.body.classList.add("impact-report-route");
    return () => {
      document.body.classList.remove("impact-report-route");
    };
  }, []);

  useEffect(() => {
    const loadCommunityRequests = async () => {
      if (config?.ENABLE_IMPACT_REPORT !== "true") return;

      if (config?.IMPACT_REPORT_USE_MOCK_DATA === "true") {
        setCommunityRequests(mockCommunityRequests);
        return;
      }

      try {
        const response = await fetchCommunityRequests(api);
        setCommunityRequests(response.data);
      } catch (error) {
        console.error("Error fetching community requests:", error);
        setCommunityRequests([]);
      }

      try {
        const response = await fetchCommunityQuarterlySummary(api);
        setQuarterlySummary(response.data);
      } catch (error) {
        console.error("Error fetching community quarterly summary:", error);
        setQuarterlySummary({});
      }
    };

    loadCommunityRequests();
  }, [config, api]);

  useEffect(() => {
    if (config?.ENABLE_IMPACT_REPORT !== "true") return;

    const fetchAndScheduleRefresh = async () => {
      // Use mock data in local development
      if (config?.IMPACT_REPORT_USE_MOCK_DATA === "true") {
        setExportUrl(mockExportUrlResponse.exportUrl);
        setIsLoadingExport(false);
        setHasExportError(false);
        return;
      }

      // Initial fetch with retry logic
      let response;
      try {
        response = await fetchWithRetry(() => fetchExportUrl(api));
      } catch (error) {
        console.error("All export URL fetch attempts failed:", error);
        setHasExportError(true);
        setIsLoadingExport(false);
        return;
      }

      setExportUrl(response.data.exportUrl);
      setIsLoadingExport(false);
      setHasExportError(false);

      // Schedule refresh 5 minutes before URL expires
      const delay = calculateRefreshDelay(response.data.expiresAt);

      const timeoutId = setTimeout(async () => {
        setIsLoadingExport(true);

        try {
          const refreshResponse = await fetchWithRetry(() =>
            fetchExportUrl(api)
          );
          setExportUrl(refreshResponse.data.exportUrl);
          setIsLoadingExport(false);
          setHasExportError(false);
        } catch (error) {
          console.error("All export URL refresh attempts failed:", error);
          setHasExportError(true);
          setIsLoadingExport(false);
        }
      }, delay);

      return () => clearTimeout(timeoutId);
    };

    const cleanup = fetchAndScheduleRefresh();
    return () => {
      cleanup.then(cleanupFn => cleanupFn && cleanupFn());
    };
  }, [config, api]);

  if (config?.ENABLE_IMPACT_REPORT !== "true") {
    return <NotFound />;
  }

  return (
    <Container>
      <Hero
        mobileImageUrl={mobileHeroImage}
        desktopImageUrl={desktopHeroImage}
      />
      <Carousel />
      <TextDetail />
      <SummaryMetrics
        quarterlySummary={quarterlySummary}
        communityRequests={communityRequests}
      />
      <ContentWrapper>
        <CommunityRequestsTable
          communityRequests={communityRequests}
          exportUrl={exportUrl}
          isLoadingExport={isLoadingExport}
          hasExportError={hasExportError}
        />
      </ContentWrapper>
      <FaqAccordion />
    </Container>
  );
};

export default ImpactReportContainer;
